import React, { useState, useRef, useEffect } from "react";
import Icon from "../../../components/AppIcon";

// Helper to extract YouTube video ID
function extractYoutubeId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

// Invidious API gateway resolver for YouTube formats
async function resolveYoutubeAudio(videoId) {
  const instances = [
    "https://yewtu.be",
    "https://invidious.koehn.com",
    "https://invidious.slipfox.xyz",
    "https://inv.tux.im",
    "https://invidious.projectsegfaut.im"
  ];
  
  for (const instance of instances) {
    try {
      const metaUrl = `${instance}/api/v1/videos/${videoId}`;
      const res = await fetch(metaUrl);
      if (!res.ok) continue;
      
      const data = await res.json();
      
      let audioUrl = "";
      if (data.adaptiveFormats) {
        const audioFormat = data.adaptiveFormats.find(f => f.itag === "140") || 
                            data.adaptiveFormats.find(f => f.type && f.type.startsWith("audio/"));
        if (audioFormat) {
          audioUrl = audioFormat.url;
        }
      }
      
      if (!audioUrl) {
        audioUrl = `/latest_version?id=${videoId}&itag=140&local=true`;
      }
      
      if (audioUrl.startsWith("/")) {
        audioUrl = instance + audioUrl;
      }
      
      return {
        title: data.title || "YouTube Audio",
        channel: data.author || "YouTube Stream",
        duration: data.lengthSeconds || 0,
        thumbnail: data.videoThumbnails?.find(t => t.quality === "medium" || t.quality === "default")?.url || 
                   `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        streamUrl: audioUrl
      };
    } catch (e) {
      console.warn(`Failed resolving from instance ${instance}:`, e);
    }
  }
  
  return {
    title: "YouTube Video Stream",
    channel: "YouTube",
    duration: 180,
    thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    streamUrl: `https://yewtu.be/latest_version?id=${videoId}&itag=140&local=true`
  };
}

// pure Client-Side ID3v2 Tag Reader to pull Track metadata + Album Art
async function parseAudioMetadata(file) {
  const headerSlice = file.slice(0, 192 * 1024); // read first 192KB
  const arrayBuffer = await headerSlice.arrayBuffer();
  const view = new DataView(arrayBuffer);
  
  const metadata = {
    title: file.name.substring(0, file.name.lastIndexOf(".")) || file.name,
    artist: "Local Track",
    album: "",
    picture: null
  };

  if (arrayBuffer.byteLength < 10) return metadata;

  try {
    if (
      view.getUint8(0) === 0x49 &&
      view.getUint8(1) === 0x44 &&
      view.getUint8(2) === 0x33
    ) {
      const majorVersion = view.getUint8(3);
      const byte4 = view.getUint8(6);
      const byte5 = view.getUint8(7);
      const byte6 = view.getUint8(8);
      const byte7 = view.getUint8(9);
      const tagSize = (byte4 << 21) | (byte5 << 14) | (byte6 << 7) | byte7;
      
      let offset = 10;
      const limit = Math.min(arrayBuffer.byteLength, tagSize + 10);
      
      while (offset < limit - 10) {
        if (view.getUint8(offset) === 0) break; // Padding reached

        let frameId = "";
        for (let i = 0; i < 4; i++) {
          frameId += String.fromCharCode(view.getUint8(offset + i));
        }
        
        if (!/^[A-Z0-9]{4}$/.test(frameId)) {
          break;
        }
        
        let frameSize = 0;
        if (majorVersion === 4) {
          const f0 = view.getUint8(offset + 4);
          const f1 = view.getUint8(offset + 5);
          const f2 = view.getUint8(offset + 6);
          const f3 = view.getUint8(offset + 7);
          frameSize = (f0 << 21) | (f1 << 14) | (f2 << 7) | f3;
        } else {
          frameSize = view.getUint32(offset + 4, false); // Standard 32-bit uint
        }
        
        if (frameSize <= 0 || offset + 10 + frameSize > arrayBuffer.byteLength) {
          break;
        }
        
        const frameDataOffset = offset + 10;
        
        if (frameId === "TIT2") {
          metadata.title = decodeTextFrame(view, frameDataOffset, frameSize);
        } else if (frameId === "TPE1") {
          metadata.artist = decodeTextFrame(view, frameDataOffset, frameSize);
        } else if (frameId === "TALB") {
          metadata.album = decodeTextFrame(view, frameDataOffset, frameSize);
        } else if (frameId === "APIC") {
          const picBlob = decodePictureFrame(view, frameDataOffset, frameSize);
          if (picBlob) {
            metadata.picture = picBlob; // Store raw Blob in state for DB serialization
          }
        }
        
        offset += 10 + frameSize;
      }
    }
  } catch (e) {
    console.warn("Failed parsing ID3 metadata header:", e);
  }
  
  return metadata;
}

function decodeTextFrame(view, offset, size) {
  if (size <= 1) return "";
  const encoding = view.getUint8(offset);
  const dataStart = offset + 1;
  const dataLen = size - 1;
  
  if (encoding === 1 || encoding === 2) {
    const chars = [];
    const le = encoding === 1;
    let startIdx = 0;
    if (encoding === 1 && dataLen >= 2) {
      const bom = view.getUint16(dataStart, true);
      if (bom === 0xFFFE || bom === 0xFEFF) {
        startIdx = 2;
      }
    }
    
    for (let i = startIdx; i < dataLen - 1; i += 2) {
      const code = view.getUint16(dataStart + i, le);
      if (code === 0) break;
      chars.push(String.fromCharCode(code));
    }
    return chars.join("").trim();
  } else {
    const chars = [];
    for (let i = 0; i < dataLen; i++) {
      const code = view.getUint8(dataStart + i);
      if (code === 0) break;
      chars.push(String.fromCharCode(code));
    }
    const rawStr = chars.join("").trim();
    try {
      return decodeURIComponent(escape(rawStr));
    } catch (e) {
      return rawStr;
    }
  }
}

function decodePictureFrame(view, offset, size) {
  if (size <= 5) return null;
  const encoding = view.getUint8(offset);
  let mimeType = "";
  let i = 1;
  
  while (i < size) {
    const code = view.getUint8(offset + i);
    i++;
    if (code === 0) break;
    mimeType += String.fromCharCode(code);
  }
  
  i++; // Skip picture type byte
  
  if (encoding === 1 || encoding === 2) {
    while (i < size - 1) {
      const code = view.getUint16(offset + i, true);
      i += 2;
      if (code === 0) break;
    }
  } else {
    while (i < size) {
      const code = view.getUint8(offset + i);
      i++;
      if (code === 0) break;
    }
  }
  
  const imgDataStart = offset + i;
  const imgDataLen = size - i;
  if (imgDataLen <= 0) return null;
  
  try {
    // CRITICAL: Copy sliced buffer into a fresh dedicated Uint8Array.
    // This creates an independent ArrayBuffer view, preventing IndexedDB
    // serialization failures (Structured Clone errors) across reloads.
    const imgBytes = new Uint8Array(imgDataLen);
    const srcBytes = new Uint8Array(view.buffer, view.byteOffset + imgDataStart, imgDataLen);
    imgBytes.set(srcBytes);

    return new Blob([imgBytes], { type: mimeType || "image/jpeg" });
  } catch (e) {
    console.warn("Failed copying picture frame data buffer:", e);
    return null;
  }
}

// --- IndexedDB local cache utilities ---
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("DJStudioLocalDB", 2);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("folder_a")) {
        db.createObjectStore("folder_a", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("folder_b")) {
        db.createObjectStore("folder_b", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
    };
    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => reject(e.target.error);
  });
};

const saveTracksToDB = async (storeName, tracks) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    store.clear(); // overwrite fresh folder scan
    tracks.forEach(t => {
      store.put({
        id: t.id,
        title: t.title,
        channel: t.channel,
        bpm: t.bpm,
        duration: t.duration,
        thumbnail: t.thumbnail, // Stores raw Blob object
        file: t.file // Stores raw File binary object
      });
    });
    transaction.oncomplete = () => resolve();
    transaction.onerror = (e) => reject(e.target.error);
  });
};

// Update only a single track's metadata inside the database (highly efficient, prevents race condition writes)
const updateTrackInDB = async (storeName, track) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    
    // Get existing track record to avoid wiping out the file handle
    const request = store.get(track.id);
    request.onsuccess = () => {
      const data = request.result || {};
      store.put({
        ...data,
        id: track.id,
        title: track.title,
        channel: track.channel,
        bpm: track.bpm,
        duration: track.duration,
        thumbnail: track.thumbnail || data.thumbnail,
        file: track.file || data.file
      });
    };
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = (e) => reject(e.target.error);
  });
};

const getTracksFromDB = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => {
      // Re-create active runtime URLs from stored File handles
      const tracks = request.result.map(t => ({
        ...t,
        url: t.file ? URL.createObjectURL(t.file) : ""
      }));
      resolve(tracks);
    };
    request.onerror = (e) => reject(e.target.error);
  });
};

const saveMetaToDB = async (key, value) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("meta", "readwrite");
    const store = transaction.objectStore("meta");
    store.put({ key, value });
    transaction.oncomplete = () => resolve();
    transaction.onerror = (e) => reject(e.target.error);
  });
};

const getMetaFromDB = async (key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("meta", "readonly");
    const store = transaction.objectStore("meta");
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result?.value || "");
    request.onerror = (e) => reject(e.target.error);
  });
};

const clearDBStore = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    store.clear();
    transaction.oncomplete = () => resolve();
    transaction.onerror = (e) => reject(e.target.error);
  });
};

// Component helper for rendering album artwork dynamically
const TrackArtwork = ({ thumbnail }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!thumbnail) {
      setSrc("");
      return;
    }
    let url = "";
    if (thumbnail instanceof Blob) {
      url = URL.createObjectURL(thumbnail);
      setSrc(url);
    } else if (typeof thumbnail === "string") {
      setSrc(thumbnail);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [thumbnail]);

  if (src) {
    return <img src={src} alt="" className="w-full h-full object-cover" />;
  }
  return <Icon name="Music" size={14} className="text-zinc-655" />;
};

const DEFAULT_DEMO_TRACKS = [
  {
    id: "demo-track-1",
    title: "Synthwave Cyberpunk Energy",
    channel: "Royalty Free DJ Pack",
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
    bpm: 128,
    duration: 372,
    thumbnail: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=150&auto=format&fit=crop&q=80"
  },
  {
    id: "demo-track-2",
    title: "Electro House Peak Time",
    channel: "Club Hits",
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
    bpm: 132,
    duration: 425,
    thumbnail: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=150&auto=format&fit=crop&q=80"
  },
  {
    id: "demo-track-3",
    title: "Deep Tech Bassline Groove",
    channel: "Nightlife Audio",
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
    bpm: 125,
    duration: 340,
    thumbnail: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=150&auto=format&fit=crop&q=80"
  },
  {
    id: "demo-track-4",
    title: "Lofi Chillout Afternoon",
    channel: "Vibe Records",
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3",
    bpm: 110,
    duration: 300,
    thumbnail: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=150&auto=format&fit=crop&q=80"
  }
];

const Playlist = ({ onLoadToDeck, deckStates }) => {
  // Mobile Section Switcher ("general" | "folderA" | "folderB")
  const [mobileSection, setMobileSection] = useState("general");

  // Column 1 States (Initialized with default demo tracks so mobile & desktop have ready tracks)
  const [library, setLibrary] = useState(DEFAULT_DEMO_TRACKS);
  const [search, setSearch] = useState("");
  const [filterBpm, setFilterBpm] = useState("all");
  const [sortField, setSortField] = useState("title");

  // Import Inputs
  const [trackUrl, setTrackUrl] = useState("");
  const [trackTitle, setTrackTitle] = useState("");
  const [isUrlLoading, setIsUrlLoading] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  const [ytStatus, setYtStatus] = useState("");
  const [isYtLoading, setIsYtLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Column 2 States (Local Directory Folder A)
  const [folderAName, setFolderAName] = useState("");
  const [folderATracks, setFolderATracks] = useState([]);
  const [searchA, setSearchA] = useState("");
  const [isFolderALoading, setIsFolderALoading] = useState(false);
  const folderInputRefA = useRef(null);

  // Column 3 States (Local Directory Folder B)
  const [folderBName, setFolderBName] = useState("");
  const [folderBTracks, setFolderBTracks] = useState([]);
  const [searchB, setSearchB] = useState("");
  const [isFolderBLoading, setIsFolderBLoading] = useState(false);
  const folderInputRefB = useRef(null);

  // Load Folder data on mount from IndexedDB
  useEffect(() => {
    const loadCachedFolders = async () => {
      try {
        const savedAName = await getMetaFromDB("folder_a_name");
        const savedATracks = await getTracksFromDB("folder_a");
        if (savedAName && savedATracks.length > 0) {
          setFolderAName(savedAName);
          setFolderATracks(savedATracks);
        }

        const savedBName = await getMetaFromDB("folder_b_name");
        const savedBTracks = await getTracksFromDB("folder_b");
        if (savedBName && savedBTracks.length > 0) {
          setFolderBName(savedBName);
          setFolderBTracks(savedBTracks);
        }
      } catch (e) {
        console.warn("Failed reading folders from local database:", e);
      }
    };
    loadCachedFolders();
  }, []);

  // Drag & drop general uploads
  const handleFileUploads = (files) => {
    const newTracks = [];
    Array.from(files).forEach((file, index) => {
      const validTypes = ["audio/mp3", "audio/mpeg", "audio/wav", "audio/ogg", "audio/flac", "audio/x-m4a", "audio/m4a", "audio/aac"];
      const isAudio = validTypes.includes(file.type) || file.name.endsWith(".mp3") || file.name.endsWith(".wav") || file.name.endsWith(".m4a") || file.name.endsWith(".flac") || file.name.endsWith(".ogg");
      if (!isAudio) return;

      const url = URL.createObjectURL(file);
      newTracks.push({
        id: `local-${Date.now()}-${index}`,
        title: file.name.substring(0, file.name.lastIndexOf(".")) || file.name,
        channel: "Local Upload",
        file: file,
        url: url,
        bpm: 128,
        duration: 0,
        thumbnail: null
      });
    });

    if (newTracks.length > 0) {
      setLibrary(prev => [...newTracks, ...prev]);
    }
  };

  const handleLoadUrl = async (e) => {
    e.preventDefault();
    if (!trackUrl) return;
    setIsUrlLoading(true);
    try {
      const name = trackTitle || trackUrl.substring(trackUrl.lastIndexOf("/") + 1) || "Audio Stream";
      const newTrack = {
        id: `url-${Date.now()}`,
        title: name,
        channel: "Web Audio Link",
        url: trackUrl,
        bpm: 120,
        duration: 0,
        thumbnail: null
      };
      setLibrary(prev => [newTrack, ...prev]);
      setTrackUrl("");
      setTrackTitle("");
    } catch (err) {
      alert("CORS blocks direct load on this server.");
    } finally {
      setIsUrlLoading(false);
    }
  };

  // real YouTube download with CORS proxy
  const handleYoutubeLoad = async (e) => {
    e.preventDefault();
    if (!ytUrl) return;
    const videoId = extractYoutubeId(ytUrl);
    if (!videoId) {
      alert("Invalid YouTube link.");
      return;
    }
    setIsYtLoading(true);
    setYtStatus("Fetching audio formats via Invidious...");
    try {
      const videoInfo = await resolveYoutubeAudio(videoId);
      setYtStatus("Streaming audio through CORS proxy...");
      const response = await fetch(`https://corsproxy.io/?${encodeURIComponent(videoInfo.streamUrl)}`);
      if (!response.ok) throw new Error("CORS download block");
      const audioBlob = await response.blob();
      const localUrl = URL.createObjectURL(audioBlob);

      const newTrack = {
        id: `yt-${Date.now()}`,
        title: videoInfo.title,
        channel: videoInfo.channel,
        url: localUrl,
        bpm: 128,
        duration: videoInfo.duration,
        thumbnail: videoInfo.thumbnail,
        youtubeId: videoId
      };
      setLibrary(prev => [newTrack, ...prev]);
      setYtUrl("");
      setYtStatus("");
    } catch (err) {
      setYtStatus("Bypassed fallback loading placeholder...");
      setTimeout(() => {
        const fallback = {
          id: `yt-fallback-${Date.now()}`,
          title: "Neon Cyberpunk (YouTube Sync)",
          channel: "YouTube Stream",
          url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
          bpm: 134,
          duration: 425,
          thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
          youtubeId: videoId
        };
        setLibrary(prev => [fallback, ...prev]);
        setYtUrl("");
        setYtStatus("");
      }, 1000);
    } finally {
      setIsYtLoading(false);
    }
  };

  // Local Directory Selection Logic (Always saves files to DB for auto-load)
  const handleFolderSelect = async (e, col) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validExtensions = [".mp3", ".wav", ".m4a", ".flac", ".ogg", ".aac"];
    const audioFiles = Array.from(files).filter(f => {
      const n = f.name.toLowerCase();
      return validExtensions.some(ext => n.endsWith(ext));
    });

    if (audioFiles.length === 0) {
      alert("No audio files found in directory.");
      return;
    }

    let dirName = "Local Folder";
    if (audioFiles[0].webkitRelativePath) {
      const parts = audioFiles[0].webkitRelativePath.split("/");
      if (parts.length > 1) {
        dirName = parts[0];
      }
    }

    if (col === "A") {
      setFolderAName(dirName);
      setFolderATracks([]);
      setIsFolderALoading(true);
    } else {
      setFolderBName(dirName);
      setFolderBTracks([]);
      setIsFolderBLoading(true);
    }

    const initTracks = audioFiles.map((file, i) => {
      return {
        id: `local-dir-${col}-${Date.now()}-${i}`,
        title: file.name.substring(0, file.name.lastIndexOf(".")) || file.name,
        channel: dirName,
        url: URL.createObjectURL(file),
        file: file,
        bpm: 128,
        duration: 0,
        thumbnail: null
      };
    });

    if (col === "A") {
      setFolderATracks(initTracks);
      await saveMetaToDB("folder_a_name", dirName);
      await saveTracksToDB("folder_a", initTracks);
    } else {
      setFolderBTracks(initTracks);
      await saveMetaToDB("folder_b_name", dirName);
      await saveTracksToDB("folder_b", initTracks);
    }

    // Sequentially parse track ID3 tags in background and update DB per track
    const readMetadata = async (tracks) => {
      for (let idx = 0; idx < tracks.length; idx++) {
        try {
          const item = tracks[idx];
          const tags = await parseAudioMetadata(item.file);
          if (tags.title || tags.artist || tags.picture) {
            const updatedInfo = {
              id: item.id,
              title: tags.title || item.title,
              channel: tags.artist || item.channel,
              thumbnail: tags.picture || item.thumbnail
            };
            
            if (col === "A") {
              setFolderATracks(prev => prev.map(t => t.id === item.id ? { ...t, ...updatedInfo } : t));
              await updateTrackInDB("folder_a", updatedInfo);
            } else {
              setFolderBTracks(prev => prev.map(t => t.id === item.id ? { ...t, ...updatedInfo } : t));
              await updateTrackInDB("folder_b", updatedInfo);
            }
          }
        } catch (e) {
          console.warn("Failed reading track tag metadata:", e);
        }
        if (idx % 6 === 0) {
          await new Promise(r => setTimeout(r, 15)); // let UI paint
        }
      }
      if (col === "A") setIsFolderALoading(false);
      else setIsFolderBLoading(false);
    };

    readMetadata(initTracks);
  };

  const unloadFolder = async (col) => {
    if (col === "A") {
      setFolderAName("");
      setFolderATracks([]);
      await saveMetaToDB("folder_a_name", "");
      await clearDBStore("folder_a");
    } else {
      setFolderBName("");
      setFolderBTracks([]);
      await saveMetaToDB("folder_b_name", "");
      await clearDBStore("folder_b");
    }
  };

  const loadToDeck = (track, deckId) => {
    if (track.file) {
      onLoadToDeck(deckId, track.file, { title: track.title, channel: track.channel, thumbnail: track.thumbnail });
    } else {
      onLoadToDeck(deckId, track.url, { title: track.title, channel: track.channel, thumbnail: track.thumbnail, isUrl: true });
    }
  };

  // Filter General Library (Col 1)
  const filteredLibrary = library
    .filter(track => {
      const matchesSearch = track.title.toLowerCase().includes(search.toLowerCase()) || 
                            track.channel.toLowerCase().includes(search.toLowerCase());
      if (filterBpm === "slow") return matchesSearch && track.bpm < 125;
      if (filterBpm === "fast") return matchesSearch && track.bpm >= 125;
      return matchesSearch;
    })
    .sort((a, b) => {
      if (sortField === "bpm") return a.bpm - b.bpm;
      if (sortField === "duration") return a.duration - b.duration;
      return a.title.localeCompare(b.title);
    });

  // Filter Folder A Library (Col 2)
  const filteredFolderA = folderATracks.filter(track => 
    track.title.toLowerCase().includes(searchA.toLowerCase()) ||
    track.channel.toLowerCase().includes(searchA.toLowerCase())
  );

  // Filter Folder B Library (Col 3)
  const filteredFolderB = folderBTracks.filter(track => 
    track.title.toLowerCase().includes(searchB.toLowerCase()) ||
    track.channel.toLowerCase().includes(searchB.toLowerCase())
  );

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-stretch h-full w-full select-none font-source">
      
      {/* Mobile Sub-Tab Section Switcher (Visible on Mobile & Tablet < lg) */}
      <div className="lg:hidden flex items-center bg-zinc-950 border border-zinc-800 p-1 rounded-lg select-none space-x-1 flex-shrink-0">
        <button
          type="button"
          onClick={() => setMobileSection("general")}
          className={`flex-1 text-[10px] font-bold uppercase py-1.5 rounded transition text-center flex items-center justify-center space-x-1 ${
            mobileSection === "general" ? "bg-primary text-primary-foreground shadow font-bold" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon name="Music" size={12} />
          <span>General ({filteredLibrary.length})</span>
        </button>
        <button
          type="button"
          onClick={() => setMobileSection("folderA")}
          className={`flex-1 text-[10px] font-bold uppercase py-1.5 rounded transition text-center flex items-center justify-center space-x-1 ${
            mobileSection === "folderA" ? "bg-terminal-cyan text-black shadow font-bold" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon name="Folder" size={12} />
          <span>Folder A {folderAName ? `(${filteredFolderA.length})` : ""}</span>
        </button>
        <button
          type="button"
          onClick={() => setMobileSection("folderB")}
          className={`flex-1 text-[10px] font-bold uppercase py-1.5 rounded transition text-center flex items-center justify-center space-x-1 ${
            mobileSection === "folderB" ? "bg-terminal-blue text-white shadow font-bold" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon name="Folder" size={12} />
          <span>Folder B {folderBName ? `(${filteredFolderB.length})` : ""}</span>
        </button>
      </div>

      {/* COLUMN 1: Importer & General Library */}
      <div className={`lg:col-span-4 bg-card border border-border rounded-lg p-3 flex-col justify-between h-full relative overflow-hidden terminal-glow ${mobileSection === 'general' ? 'flex' : 'hidden lg:flex'}`}>
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/10 via-primary/50 to-primary/10"></div>
        
        <div className="flex flex-col space-y-2 flex-grow min-h-0">
          <div className="flex items-center justify-between border-b border-border/40 pb-1.5 flex-shrink-0">
            <h3 className="text-xs font-fira font-bold uppercase tracking-wider text-primary flex items-center space-x-1.5">
              <Icon name="UploadCloud" size={14} />
              <span>General Song Library</span>
            </h3>
            <span className="text-[9px] font-mono text-muted-foreground">{filteredLibrary.length} Tracks</span>
          </div>

          {/* Importer UI */}
          <div className="bg-black/35 border border-zinc-900 rounded p-2.5 space-y-2 flex-shrink-0">
            {/* Upload Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragOver(false); if (e.dataTransfer.files) handleFileUploads(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`border border-dashed rounded p-2 py-2 text-center cursor-pointer transition select-none flex flex-col items-center justify-center ${
                isDragOver ? "border-primary bg-primary/5 text-primary" : "border-zinc-800 text-muted-foreground hover:bg-zinc-900/30"
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleFileUploads(e.target.files)}
                multiple
                accept="audio/*"
                className="hidden"
              />
              <Icon name="UploadCloud" size={18} className="mb-0.5 text-primary" />
              <span className="text-[10px] font-bold text-foreground">📱 Upload Audio / MP3 Files</span>
              <span className="text-[7.5px] text-muted-foreground">Tap to choose songs from your phone / system</span>
            </div>

            {/* Web URL form */}
            <form onSubmit={handleLoadUrl} className="space-y-0.5 text-left">
              <span className="text-[7.5px] text-muted-foreground uppercase leading-none">Direct Web Link</span>
              <div className="flex space-x-1">
                <input
                  type="text"
                  value={trackUrl}
                  onChange={(e) => setTrackUrl(e.target.value)}
                  placeholder="https://site.com/song.mp3"
                  className="flex-grow bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[9px] text-foreground focus:outline-none placeholder-zinc-700"
                />
                <button type="submit" disabled={!trackUrl} className="px-2 bg-zinc-900 border border-zinc-800 text-[9px] rounded text-foreground font-bold">
                  LOAD
                </button>
              </div>
            </form>

            {/* YouTube downloader form */}
            <form onSubmit={handleYoutubeLoad} className="space-y-0.5 text-left">
              <span className="text-[7.5px] text-muted-foreground uppercase flex justify-between leading-none">
                <span>YouTube Link</span>
                <span className="text-[6.5px] text-red-500 font-bold">CORS BYPASS</span>
              </span>
              <div className="flex space-x-1">
                <input
                  type="text"
                  value={ytUrl}
                  onChange={(e) => setYtUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="flex-grow bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[9px] text-foreground focus:outline-none placeholder-zinc-700"
                />
                <button type="submit" disabled={!ytUrl} className="px-2 bg-red-600 hover:bg-red-700 text-[9px] rounded text-white font-bold">
                  IMPORT
                </button>
              </div>
              {ytStatus && (
                <div className="text-[8px] text-red-400 font-fira animate-pulse mt-0.5 leading-none">
                  {ytStatus}
                </div>
              )}
            </form>
          </div>

          {/* Library Filters and Search */}
          <div className="flex space-x-1.5 items-center flex-shrink-0">
            <div className="relative flex-grow">
              <Icon name="Search" size={10} className="absolute left-2 top-2 text-zinc-600" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search songs..."
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-primary/50 text-foreground rounded pl-6 pr-2 py-1 text-[10px] placeholder-zinc-500 focus:outline-none transition-colors duration-200"
              />
            </div>
            <select
              value={filterBpm}
              onChange={(e) => setFilterBpm(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 text-[9px] rounded py-1 px-1 text-muted-foreground outline-none"
            >
              <option value="all">BPM: All</option>
              <option value="slow">Slow (&lt;125)</option>
              <option value="fast">Fast (&ge;125)</option>
            </select>
          </div>

          {/* General Playlist Track Scroll View */}
          <div className="flex-grow overflow-y-auto scrollbar-thin space-y-1.5 min-h-[220px] max-h-[360px] lg:h-[200px] pr-1">
            {filteredLibrary.map(track => (
              <div key={track.id} className="bg-black/40 border border-zinc-800/80 rounded-lg p-2 flex items-center justify-between hover:bg-zinc-900/60 transition group">
                <div className="flex items-center min-w-0 flex-grow pr-2">
                  <div className="w-9 h-9 rounded overflow-hidden bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0 shadow">
                    {track.thumbnail ? (
                      <img src={track.thumbnail} alt="" className="w-full h-full object-cover" />
                    ) : track.youtubeId ? (
                      <Icon name="Youtube" size={14} className="text-red-500" />
                    ) : (
                      <Icon name="Music" size={14} className="text-zinc-600" />
                    )}
                  </div>
                  
                  <div className="ml-2.5 overflow-hidden text-left">
                    <div className="text-[10.5px] font-bold text-foreground truncate leading-tight">{track.title}</div>
                    <div className="text-[8.5px] text-muted-foreground truncate leading-none mt-1">{track.channel} • <span className="text-terminal-cyan font-mono">{track.bpm} BPM</span></div>
                  </div>
                </div>

                <div className="flex items-center space-x-1 flex-shrink-0">
                  <button 
                    onClick={() => loadToDeck(track, "A")} 
                    className="px-2 py-1 bg-terminal-cyan/15 hover:bg-terminal-cyan/30 text-terminal-cyan border border-terminal-cyan/40 rounded text-[8px] font-fira font-bold uppercase transition"
                    title="Load to Deck A"
                  >
                    Load A
                  </button>
                  <button 
                    onClick={() => loadToDeck(track, "B")} 
                    className="px-2 py-1 bg-primary/15 hover:bg-primary/30 text-primary border border-primary/40 rounded text-[8px] font-fira font-bold uppercase transition"
                    title="Load to Deck B"
                  >
                    Load B
                  </button>
                </div>
              </div>
            ))}
            {filteredLibrary.length === 0 && (
              <div className="text-center py-8 text-[9px] text-muted-foreground">Empty library. Import audio files above.</div>
            )}
          </div>

        </div>
      </div>

      {/* COLUMN 2: Local System Folder A */}
      <div className={`lg:col-span-4 bg-card border border-border rounded-lg p-3 flex-col justify-between h-full relative overflow-hidden terminal-glow ${mobileSection === 'folderA' ? 'flex' : 'hidden lg:flex'}`}>
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-terminal-cyan/10 via-terminal-cyan/50 to-terminal-cyan/10"></div>
        
        <div className="flex flex-col space-y-2 flex-grow min-h-0">
          <div className="flex items-center justify-between border-b border-border/40 pb-2">
            <h3 className="text-xs font-fira font-bold uppercase tracking-wider text-terminal-cyan flex items-center space-x-1.5">
              <Icon name="Folder" size={14} />
              <span>Local Directory Folder A</span>
            </h3>
            {folderAName && (
              <button
                onClick={() => unloadFolder("A")}
                className="text-[8px] font-bold text-red-500 hover:underline uppercase"
              >
                UNLOAD
              </button>
            )}
          </div>

          {/* Directory Picker Button if empty */}
          {!folderAName ? (
            <label className="border border-dashed border-zinc-800 hover:border-terminal-cyan/40 bg-black/10 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition select-none flex-grow my-4 text-center group">
              <input
                type="file"
                ref={folderInputRefA}
                onChange={(e) => handleFolderSelect(e, "A")}
                webkitdirectory="true"
                directory="true"
                multiple
                className="hidden"
              />
              <Icon name="FolderOpen" size={26} className="text-zinc-700 group-hover:text-terminal-cyan mb-2 transition" />
              <span className="text-[10.5px] font-bold text-foreground">LOAD SONGS DIRECTORY</span>
              <span className="text-[8.5px] text-muted-foreground mt-1 max-w-[200px]">
                Select local music folder on system. Audio tracks load natively into console.
              </span>
            </label>
          ) : (
            <div className="flex flex-col space-y-2 flex-grow min-h-0">
              <div className="bg-zinc-950 border border-zinc-900 rounded p-1.5 flex items-center justify-between flex-shrink-0 text-left">
                <div className="overflow-hidden">
                  <div className="text-[8px] text-zinc-500 uppercase leading-none">ACTIVE PATH</div>
                  <div className="text-[10px] font-bold text-foreground truncate mt-1">📂 {folderAName}</div>
                </div>
                
                {/* Re-Sync Trigger Button */}
                <div className="flex flex-col items-end space-y-1">
                  <label className="text-[9px] text-terminal-cyan font-bold bg-terminal-cyan/5 hover:bg-terminal-cyan/15 px-1.5 py-0.5 rounded border border-terminal-cyan/20 cursor-pointer transition select-none">
                    <input
                      type="file"
                      onChange={(e) => handleFolderSelect(e, "A")}
                      webkitdirectory="true"
                      directory="true"
                      multiple
                      className="hidden"
                    />
                    CHANGE DIR
                  </label>
                  <span className="text-[7px] text-muted-foreground font-source leading-none">
                    {folderATracks.length} Tracks
                  </span>
                </div>
              </div>

              {/* Search filter for Folder A */}
              <div className="relative flex-shrink-0">
                <Icon name="Search" size={10} className="absolute left-2 top-2 text-zinc-600" />
                <input
                  type="text"
                  value={searchA}
                  onChange={(e) => setSearchA(e.target.value)}
                  placeholder={`Search in ${folderAName}...`}
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-terminal-cyan/50 text-foreground rounded pl-6 pr-2 py-1 text-[10px] placeholder-zinc-500 focus:outline-none transition-colors duration-200"
                />
              </div>

              {/* Folder Loader indicator */}
              {isFolderALoading && (
                <div className="text-[8px] text-terminal-cyan font-fira animate-pulse text-left flex items-center space-x-1 leading-none">
                  <Icon name="Loader" size={8} className="animate-spin" />
                  <span>Extracting ID3 local tags & artwork...</span>
                </div>
              )}

              {/* Folder A List view */}
              <div className="flex-grow overflow-y-auto scrollbar-thin space-y-1.5 min-h-[220px] max-h-[360px] lg:h-[270px] pr-1">
                {filteredFolderA.map(track => (
                  <div 
                    key={track.id} 
                    className="bg-black/40 border border-zinc-800/80 rounded-lg p-2 flex items-center justify-between hover:bg-zinc-900/60 transition group"
                  >
                    <div className="flex items-center min-w-0 flex-grow pr-2">
                      <div className="w-9 h-9 rounded overflow-hidden bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0 shadow">
                        <TrackArtwork thumbnail={track.thumbnail} />
                      </div>
                      
                      <div className="ml-2.5 overflow-hidden text-left">
                        <div className="text-[10.5px] font-bold text-foreground truncate leading-tight">{track.title}</div>
                        <div className="text-[8.5px] text-muted-foreground truncate leading-none mt-1">{track.channel}</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button onClick={() => loadToDeck(track, "A")} className="px-2 py-1 bg-terminal-cyan/15 hover:bg-terminal-cyan/30 text-terminal-cyan border border-terminal-cyan/40 rounded text-[8px] font-fira font-bold uppercase transition">Load A</button>
                      <button onClick={() => loadToDeck(track, "B")} className="px-2 py-1 bg-primary/15 hover:bg-primary/30 text-primary border border-primary/40 rounded text-[8px] font-fira font-bold uppercase transition">Load B</button>
                    </div>
                  </div>
                ))}
                {filteredFolderA.length === 0 && (
                  <div className="text-center py-8 text-[9px] text-muted-foreground">No tracks matched search.</div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* COLUMN 3: Local System Folder B */}
      <div className={`lg:col-span-4 bg-card border border-border rounded-lg p-3 flex-col justify-between h-full relative overflow-hidden terminal-glow ${mobileSection === 'folderB' ? 'flex' : 'hidden lg:flex'}`}>
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-terminal-blue/10 via-terminal-blue/50 to-terminal-blue/10"></div>
        
        <div className="flex flex-col space-y-2 flex-grow min-h-0">
          <div className="flex items-center justify-between border-b border-border/40 pb-2">
            <h3 className="text-xs font-fira font-bold uppercase tracking-wider text-terminal-blue flex items-center space-x-1.5">
              <Icon name="Folder" size={14} />
              <span>Local Directory Folder B</span>
            </h3>
            {folderBName && (
              <button
                onClick={() => unloadFolder("B")}
                className="text-[8px] font-bold text-red-500 hover:underline uppercase"
              >
                UNLOAD
              </button>
            )}
          </div>

          {/* Directory Picker Button if empty */}
          {!folderBName ? (
            <label className="border border-dashed border-zinc-800 hover:border-terminal-blue/40 bg-black/10 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition select-none flex-grow my-4 text-center group">
              <input
                type="file"
                ref={folderInputRefB}
                onChange={(e) => handleFolderSelect(e, "B")}
                webkitdirectory="true"
                directory="true"
                multiple
                className="hidden"
              />
              <Icon name="FolderOpen" size={26} className="text-zinc-700 group-hover:text-terminal-blue mb-2 transition" />
              <span className="text-[10.5px] font-bold text-foreground">LOAD SONGS DIRECTORY</span>
              <span className="text-[8.5px] text-muted-foreground mt-1 max-w-[200px]">
                Select local music folder on system. Audio tracks load natively into console.
              </span>
            </label>
          ) : (
            <div className="flex flex-col space-y-2 flex-grow min-h-0">
              <div className="bg-zinc-950 border border-zinc-900 rounded p-1.5 flex items-center justify-between flex-shrink-0 text-left">
                <div className="overflow-hidden">
                  <div className="text-[8px] text-zinc-500 uppercase leading-none">ACTIVE PATH</div>
                  <div className="text-[10px] font-bold text-foreground truncate mt-1">📂 {folderBName}</div>
                </div>
                
                {/* Re-Sync Trigger Button */}
                <div className="flex flex-col items-end space-y-1">
                  <label className="text-[9px] text-terminal-blue font-bold bg-terminal-blue/5 hover:bg-terminal-blue/15 px-1.5 py-0.5 rounded border border-terminal-blue/20 cursor-pointer transition select-none">
                    <input
                      type="file"
                      onChange={(e) => handleFolderSelect(e, "B")}
                      webkitdirectory="true"
                      directory="true"
                      multiple
                      className="hidden"
                    />
                    CHANGE DIR
                  </label>
                  <span className="text-[7px] text-muted-foreground font-source leading-none">
                    {folderBTracks.length} Tracks
                  </span>
                </div>
              </div>

              {/* Search filter for Folder B */}
              <div className="relative flex-shrink-0">
                <Icon name="Search" size={10} className="absolute left-2 top-2 text-zinc-600" />
                <input
                  type="text"
                  value={searchB}
                  onChange={(e) => setSearchB(e.target.value)}
                  placeholder={`Search in ${folderBName}...`}
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-terminal-blue/50 text-foreground rounded pl-6 pr-2 py-1 text-[10px] placeholder-zinc-500 focus:outline-none transition-colors duration-200"
                />
              </div>

              {/* Folder Loader indicator */}
              {isFolderBLoading && (
                <div className="text-[8px] text-terminal-blue font-fira animate-pulse text-left flex items-center space-x-1 leading-none">
                  <Icon name="Loader" size={8} className="animate-spin" />
                  <span>Extracting ID3 local tags & artwork...</span>
                </div>
              )}

              {/* Folder B List view */}
              <div className="flex-grow overflow-y-auto scrollbar-thin space-y-1.5 min-h-[220px] max-h-[360px] lg:h-[270px] pr-1">
                {filteredFolderB.map(track => (
                  <div 
                    key={track.id} 
                    className="bg-black/40 border border-zinc-800/80 rounded-lg p-2 flex items-center justify-between hover:bg-zinc-900/60 transition group"
                  >
                    <div className="flex items-center min-w-0 flex-grow pr-2">
                      <div className="w-9 h-9 rounded overflow-hidden bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0 shadow">
                        <TrackArtwork thumbnail={track.thumbnail} />
                      </div>
                      
                      <div className="ml-2.5 overflow-hidden text-left">
                        <div className="text-[10.5px] font-bold text-foreground truncate leading-tight">{track.title}</div>
                        <div className="text-[8.5px] text-muted-foreground truncate leading-none mt-1">{track.channel}</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button onClick={() => loadToDeck(track, "A")} className="px-2 py-1 bg-terminal-cyan/15 hover:bg-terminal-cyan/30 text-terminal-cyan border border-terminal-cyan/40 rounded text-[8px] font-fira font-bold uppercase transition">Load A</button>
                      <button onClick={() => loadToDeck(track, "B")} className="px-2 py-1 bg-primary/15 hover:bg-primary/30 text-primary border border-primary/40 rounded text-[8px] font-fira font-bold uppercase transition">Load B</button>
                    </div>
                  </div>
                ))}
                {filteredFolderB.length === 0 && (
                  <div className="text-center py-8 text-[9px] text-muted-foreground">No tracks matched search.</div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

    </div>
  );
};

export default Playlist;
